import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckIcon, SearchIcon } from "@/components/icons";
import { JobCard } from "@/components/job-card";
import { SearchBox } from "@/components/search-box";
import { fullNumber } from "@/lib/format";
import type { SearchResponse } from "@/lib/types";
import { searchJobs } from "@/src/api";
import { useDocumentMeta } from "@/src/use-document-meta";

const quickFilters = [
  ["Remote", "", "Remote"],
  ["Engineering", "software engineer", ""],
  ["Product", "product manager", ""],
  ["Design", "designer", ""],
  ["Data", "data", ""],
  ["Marketing", "marketing", ""],
];

const PAGE_SIZE = 12;
const PUBLIC_JOB_LIMIT = 500;
const LAST_PAGE_OFFSET = Math.floor((PUBLIC_JOB_LIMIT - 1) / PAGE_SIZE) * PAGE_SIZE;

export function JobsPage() {
  const [params] = useSearchParams();
  const query = (params.get("q") || "").trim().slice(0, 120);
  const location = (params.get("location") || "").trim().slice(0, 120);
  const parsedOffset = Number.parseInt(params.get("offset") || "0", 10);
  const offset = Number.isFinite(parsedOffset)
    ? Math.min(LAST_PAGE_OFFSET, Math.floor(Math.max(0, parsedOffset) / PAGE_SIZE) * PAGE_SIZE)
    : 0;
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const hasFilters = Boolean(query || location);
  const metaTitle = query
    ? `${query} jobs${location ? ` near ${location}` : ""} | Chuno`
    : location ? `Jobs near ${location} | Chuno` : "Jobs across India | Chuno";
  const metaDescription = query
    ? `Explore current ${query} openings${location ? ` near ${location}` : ""} on Chuno.`
    : location ? `Explore current openings near ${location} on Chuno.` : "Explore current jobs from employers across India on Chuno.";

  useDocumentMeta({
    title: metaTitle,
    description: metaDescription,
    robots: hasFilters ? "noindex,follow" : "index,follow,max-image-preview:large",
    canonical: "https://www.chuno.work/jobs",
  });

  useEffect(() => {
    const controller = new AbortController();
    searchJobs({ query, location, limit: PAGE_SIZE, offset, signal: controller.signal })
      .then(setResponse)
      .catch(() => undefined);
    return () => controller.abort();
  }, [location, offset, query]);

  const total = Math.min(PUBLIC_JOB_LIMIT, response?.estimatedTotalHits ?? response?.nbHits ?? response?.hits.length ?? 0);
  const showsLatestLimit = total === PUBLIC_JOB_LIMIT;
  const hasPrevious = offset > 0;
  const hasNext = Boolean(response && offset + response.hits.length < total && offset + PAGE_SIZE < PUBLIC_JOB_LIMIT && response.hits.length === PAGE_SIZE);
  const heading = query ? `${query}${location ? ` near ${location}` : ""}` : location ? `Jobs near ${location}` : "Current opportunities";

  function pageHref(nextOffset: number) {
    const next = new URLSearchParams();
    if (query) next.set("q", query);
    if (location) next.set("location", location);
    if (nextOffset > 0) next.set("offset", String(nextOffset));
    return `/jobs${next.size ? `?${next}` : ""}`;
  }

  return (
    <main id="main" className="jobs-page">
      <section className="jobs-hero">
        <div className="shell">
          <p className="eyebrow"><span /> Find your next move</p>
          <h1>Work worth choosing.</h1>
          <p>Search current roles from hiring organisations across India.</p>
          <SearchBox key={`${query}\u0000${location}`} query={query} location={location} compact />
        </div>
      </section>
      <section className="jobs-content">
        <div className="shell">
          <div className="quick-filters" aria-label="Quick job searches">
            <span>Explore</span>
            {quickFilters.map(([label, filterQuery, filterLocation]) => {
              const search = new URLSearchParams();
              if (filterQuery) search.set("q", filterQuery);
              if (filterLocation) search.set("location", filterLocation);
              return <Link className={query === filterQuery && location === filterLocation ? "active" : ""} to={`/jobs?${search}`} key={label}>{label}</Link>;
            })}
          </div>

          <div className="results-heading">
            <div><p>{response ? `${showsLatestLimit ? "Latest " : ""}${fullNumber(total)} ${total === 1 ? "opening" : "openings"}` : "Searching live openings"}</p><h2>{heading}</h2></div>
            {response?.demo ? <span className="demo-badge">Preview data</span> : <span className="results-live"><i /> Live index</span>}
          </div>

          {!response ? (
            <div className="page-loading"><div><span />Finding current roles…</div></div>
          ) : response.hits.length ? (
            <div className="results-grid">{response.hits.map((job) => <JobCard job={job} key={job.job_id} />)}</div>
          ) : (
            <div className="empty-state">
              <div><SearchIcon /></div><h2>No exact matches yet.</h2><p>Try a broader title, a nearby city, or remove one of your search terms.</p><Link to="/jobs">Clear search <ArrowRight /></Link>
            </div>
          )}

          {response && (hasPrevious || hasNext) ? (
            <nav className="pagination" aria-label="Job results pages">
              {hasPrevious ? <Link to={pageHref(Math.max(0, offset - PAGE_SIZE))}><ArrowLeft /> Previous</Link> : <span />}
              <p>{offset + 1}–{Math.min(offset + response.hits.length, total)} of {showsLatestLimit ? "the latest " : ""}{fullNumber(total)}</p>
              {hasNext ? <Link to={pageHref(offset + PAGE_SIZE)}>Next <ArrowRight /></Link> : <span />}
            </nav>
          ) : null}

          <div className="results-assurance"><CheckIcon /><p><strong>Built for current search.</strong> Job records are refreshed as the underlying market changes.</p></div>
        </div>
      </section>
    </main>
  );
}
